import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest } from "rxjs";
import { map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { AllPlaningStatusesRequested, AllQosDegradationsRequested, AllUnitAreaMeasurementsRequested, AllUnitLengthMeasurementsRequested, selectAllPlaningStatuses, selectAllQosDegradations, selectAllUnitAreaMeasurements, selectAllUnitLengthMeasurements } from "src/app/core/linked-tables";
import { LandArea } from "src/app/core/v3/land-area";
import { LandAreaFormGroup } from "./form-group";
import { LandAreaDialogComponent, LandAreaDialogInput, LandAreaDialogOutput } from "./land-area-dialog/land-area-dialog.component";

interface State {
  lowLevelTitle?: string
  items: LandArea[]
}

@Component({
  selector: 'app-land-areas',
  templateUrl: './land-areas.component.html',
  styleUrls: ['./land-areas.component.scss'],
  providers: [ComponentStore]
})
export class LandAreasComponent implements OnInit, OnDestroy {
  private _unitOfAreaMeasurementId$ = new BehaviorSubject<number>(null)
  @Input() form!: LandAreaFormGroup

  @Input()
  set lowLevelTitle(value: string) {
    if (!value) return
    this.componentStore.patchState({lowLevelTitle: value})
  }

  @Input()
  set landAreas(value: LandArea[]) {
    this.componentStore.patchState({items: value})
  }

  @Input() isBuilding: boolean;
  @Input() 
  set unitOfAreaMeasurement(value: number) {
    this._unitOfAreaMeasurementId$.next(value)
  }

  @Output() landAreasChange = new EventEmitter<LandArea[]>()

  private _items$ = this.componentStore.select(state => state.items)

  planningStatuses$ = this.ngStore.select(selectAllPlaningStatuses)
  qosDegradations$ = this.ngStore.select(selectAllQosDegradations)
  lowLevelTitle$ = this.componentStore.select(state => state.lowLevelTitle).pipe(
    map(v => {
      return {
        title: v
      }
    })
  )

  unitOfAreaMeasurement$ = this._unitOfAreaMeasurementId$.asObservable().pipe(
    map(value => {
      if (value === null) {
        return ""
      }
      if (value === 1) {
        return " (sqm)"
      }
      return " (sqft)"
    })
  )

  dataSource$ = combineLatest([
    this._items$,
    this.ngStore.select(selectAllUnitAreaMeasurements),
    this.ngStore.select(selectAllUnitLengthMeasurements)
  ]).pipe(
    map(([items, unitAreaMeasurements, unitLengthMeasurements]) => {
      return items.map(item => {
        const unitAreaMeasurement = unitAreaMeasurements.find(x => x.id == item.unitAreaMeasurementId)
        const unitLengthMeasurement = unitLengthMeasurements.find(x => x.id == item.unitLengthMeasurementId)
        return {
          ...item,
          unitAreaMeasurementName: unitAreaMeasurement ? unitAreaMeasurement.name : 'N/A',
          unitLengthMeasurementAcronym: unitLengthMeasurement ? unitLengthMeasurement.acronym : null 
        }
      })
    }) 
  )

  constructor(
    private componentStore: ComponentStore<State>,
    private ngStore: Store,
    private dialog: MatDialog
  ) {
    this.componentStore.setState({
      items: []
    })
  }

  ngOnInit(): void {
    this.ngStore.dispatch(new AllUnitAreaMeasurementsRequested())
    this.ngStore.dispatch(new AllUnitLengthMeasurementsRequested())
    this.ngStore.dispatch(new AllPlaningStatusesRequested())
    this.ngStore.dispatch(new AllQosDegradationsRequested())
  }

  ngOnDestroy(): void {
    
  }

  onAddItem = this.componentStore.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.componentStore.state$),
      switchMap(([_, state]) => {
        const dialogRef = this.dialog.open<LandAreaDialogComponent, LandAreaDialogInput, LandAreaDialogOutput>(
          LandAreaDialogComponent,
          {
            width: '400px'
          }
        )
        return dialogRef.afterClosed().pipe(
          tap(res => {
            if (!res) return;
            const items = [...state.items, res.item]
            this.landAreasChange.emit(items)
          })
        )
      })
    )
  )

  onEditItem = this.componentStore.effect<{
    item: LandArea,
    index: number
  }>(payload$ => 
    payload$.pipe(
      withLatestFrom(this.componentStore.state$),
      switchMap(([{item, index}, state]) => {
        const dialogRef = this.dialog.open<LandAreaDialogComponent, LandAreaDialogInput, LandAreaDialogOutput>(
          LandAreaDialogComponent,
          {
            data: {item},
            width: '400px'
          }
        )
        return dialogRef.afterClosed().pipe(
          tap(res => {
            if (!res) return;
            const items = state.items.map((_item, i) => {
              if (i !== index) return _item
              return res.item
            })
            this.landAreasChange.emit(items)
          })
        )
      })
    )
  )

  onDeleteItem = this.componentStore.effect<number>(index$ => 
    index$.pipe(
      withLatestFrom(this.componentStore.state$),
      tap(([index, state]: [number, State]) => {
        const items = state.items.filter((item, i) => i !== index)
        this.landAreasChange.emit(items)
      })
    )
  )
}